import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Stack,
  Backdrop,
  CircularProgress,
  Button,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { Hub } from 'aws-amplify/utils';
import { signInWithRedirect } from 'aws-amplify/auth';

import { fetchUser, createUser } from '@/actions/user';
import { extractSession, clearAuthFailure } from '@/services/auth';
import { SET_SESSION, SET_USER } from '@/actions/types';
import {
  useAuth,
  useAuthDispatch,
  useUser,
  useUserDispatch,
} from '@/providers';

const OAUTH_CALLBACK_RETRIED_KEY = 'oauth_callback_retried';

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useAuth();
  const user = useUser();
  const authDispatch = useAuthDispatch();
  const userDispatch = useUserDispatch();
  const hasProcessedUserRef = useRef(false);
  const hasExtractedSessionRef = useRef(false);
  const recoveryStartedRef = useRef(false);
  const sessionRef = useRef(session);
  const initialSearchRef = useRef(location.search);
  const [auth_failed, setAuthFailed] = useState(false);

  sessionRef.current = session;

  const applySession = useCallback(
    (session_data) => {
      if (!session_data) {
        return false;
      }
      sessionStorage.removeItem(OAUTH_CALLBACK_RETRIED_KEY);
      clearAuthFailure();
      authDispatch({ type: SET_SESSION, session: session_data });
      setAuthFailed(false);
      return true;
    },
    [authDispatch],
  );

  const restartSignIn = async () => {
    setAuthFailed(false);
    try {
      await signInWithRedirect();
    } catch (error) {
      console.error('Error restarting sign in:', error);
      if (error?.name === 'UserAlreadyAuthenticatedException') {
        const session_data = await extractSession(false);
        if (applySession(session_data)) {
          return;
        }
      }
      setAuthFailed(true);
    }
  };

  // Complete the OAuth code exchange, then recover if it produced no session.
  // enable-oauth-listener (imported in App.js) exchanges the code. fetchAuthSession
  // waits for that exchange, so a null result means it already failed or never started.
  useEffect(() => {
    let cancelled = false;

    const recoverFromMissingSession = async () => {
      if (
        cancelled ||
        recoveryStartedRef.current ||
        sessionRef.current?.isLoggedIn
      ) {
        return;
      }
      recoveryStartedRef.current = true;

      const already_retried =
        sessionStorage.getItem(OAUTH_CALLBACK_RETRIED_KEY) === '1';
      if (!already_retried) {
        sessionStorage.setItem(OAUTH_CALLBACK_RETRIED_KEY, '1');
        try {
          await signInWithRedirect();
          return;
        } catch (error) {
          console.error('Error restarting sign in:', error);
          if (error?.name === 'UserAlreadyAuthenticatedException') {
            const session_data = await extractSession(false);
            if (!cancelled && applySession(session_data)) {
              return;
            }
          }
        }
      }

      if (!cancelled) {
        setAuthFailed(true);
      }
    };

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        extractSession(false)
          .then((session_data) => {
            if (!cancelled) {
              applySession(session_data);
            }
          })
          .catch((error) => {
            console.error('Error extracting session:', error);
          });
        return;
      }

      if (payload.event === 'signInWithRedirect_failure') {
        console.error(
          'OAuth sign-in failed:',
          payload.data?.error || payload.data,
        );
        recoverFromMissingSession();
      }
    });

    const search_params = new URLSearchParams(initialSearchRef.current);
    const oauth_error = search_params.get('error');
    if (oauth_error) {
      console.error(
        'OAuth error:',
        oauth_error,
        search_params.get('error_description'),
      );
      navigate('/', { replace: true });
      return unsubscribe;
    }

    if (sessionRef.current?.isLoggedIn || hasExtractedSessionRef.current) {
      return unsubscribe;
    }

    hasExtractedSessionRef.current = true;
    extractSession(false)
      .then((session_data) => {
        if (cancelled || sessionRef.current?.isLoggedIn) {
          return;
        }
        if (applySession(session_data)) {
          return;
        }
        recoverFromMissingSession();
      })
      .catch((error) => {
        console.error('Error extracting session:', error);
        hasExtractedSessionRef.current = false;
        recoverFromMissingSession();
      });

    return () => {
      cancelled = true;
      hasExtractedSessionRef.current = false;
      unsubscribe();
    };
  }, [applySession, navigate]);

  // When session becomes available, fetch or create user
  useEffect(() => {
    if (!session?.isLoggedIn || user || hasProcessedUserRef.current) {
      return;
    }

    hasProcessedUserRef.current = true;

    async function processUser() {
      try {
        const user_data = await fetchUser(session);
        if (user_data) {
          userDispatch({ type: SET_USER, payload: user_data });
          return;
        }

        try {
          const new_user_data = await createUser();
          if (new_user_data) {
            userDispatch({ type: SET_USER, payload: new_user_data });
          } else {
            setAuthFailed(true);
          }
        } catch (create_error) {
          console.error('Failed to create user:', create_error);
          navigate('/', { replace: true });
        }
      } catch (error) {
        // If fetchUser throws 404/401, user doesn't exist - create them
        if (error.response?.status === 404 || error.response?.status === 401) {
          try {
            const new_user_data = await createUser();
            if (new_user_data) {
              userDispatch({ type: SET_USER, payload: new_user_data });
            } else {
              setAuthFailed(true);
            }
          } catch (create_error) {
            console.error('Failed to create user:', create_error);
            navigate('/', { replace: true });
          }
        } else {
          console.error('Failed to fetch user:', error);
          setAuthFailed(true);
        }
      }
    }

    processUser();
  }, [session, user, userDispatch, navigate]);

  // When user is available, redirect based on status
  useEffect(() => {
    if (!user || !user.id) {
      return;
    }

    if (user.status === 'approved') {
      const redirect_path = localStorage.getItem('redirect_path');
      const redirect_search = localStorage.getItem('redirect_search');
      if (redirect_path && redirect_path !== '/auth/callback') {
        localStorage.removeItem('redirect_path');
        localStorage.removeItem('redirect_search');
        navigate(`${redirect_path}${redirect_search || ''}`, { replace: true });
      } else {
        localStorage.removeItem('redirect_path');
        localStorage.removeItem('redirect_search');
        navigate('/dashboard', { replace: true });
      }
    } else if (user.status === 'new') {
      navigate('/account/signup', { replace: true });
    }
    // For banned users, let ProtectedRoute handle it when they try to access protected routes
  }, [user, navigate]);

  if (auth_failed) {
    return (
      <Container>
        <Stack alignItems={'center'} spacing={2} sx={{ mt: 8 }}>
          <Typography variant='h6'>Sign-in could not be completed.</Typography>
          <Button variant='contained' color='brand' onClick={restartSignIn}>
            Try again
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open
      >
        <Stack alignItems={'center'} spacing={2}>
          <CircularProgress color='inherit' />
          <Typography variant='h6'>Analyzing</Typography>
        </Stack>
      </Backdrop>
    </Container>
  );
}
